import { ImagePaths } from '../../lib/imageUrls.js';

export const sharepointCopilotReadinessFoundation = {
    id: 50,
    slug: 'sharepoint-copilot-readiness-foundation',
    ogImage: '/og/courses/sharepoint-copilot-readiness-foundation.jpg',
    courseCode: 'SHARECOP-FND-001',
    title: 'SharePoint Copilot Readiness — Foundation',
    subtitle: 'The 1-day starter before Microsoft 365 Copilot reaches your tenant — including teams landing from SharePoint Server 2016/2019 (support ended 14 July 2026)',
    valueProposition: 'The entry-level SharePoint + Copilot readiness course mapped to the Privacy Act 1988, OAIC Notifiable Data Breaches scheme, and ACSC Essential Eight — giving site owners and M365 admins a Day-0 readiness checklist before Copilot is switched on. With SharePoint Server 2016/2019 support having ended on 14 July 2026, it is also the fastest way to make newly-migrated content Copilot-safe.',
    duration: '8 hours',
    students: 'New',
    level: 'Foundational',
    organization: 'EDUC4TE',
    image: ImagePaths.COURSE_3,
    shortDescription: 'SHARECOP-FND-001 — SharePoint Copilot readiness foundation covering RAG basics, oversharing risks, and Day-0 checklist for site owners and M365 admins.',
    price: {
        aud: 695,
        currency: 'AUD',
        earlyBird: 595
    },
    series: {
        name: 'EDUC4TE SharePoint Copilot Series',
        position: 'Foundation (entry)',
        description: 'Three-course modular series preparing SharePoint environments for Microsoft 365 Copilot — Foundation, Governance, and Enterprise Architecture.',
        courses: [
            { code: 'SHARECOP-FND-001', title: 'SharePoint Copilot Readiness — Foundation', slug: 'sharepoint-copilot-readiness-foundation' },
            { code: 'SHARECOP-GOV-001', title: 'SharePoint Copilot Governance — SAM, RCD and Permission Remediation', slug: 'sharepoint-copilot-in-a-day' },
            { code: 'SHARECOP-ARCH-001', title: 'SharePoint Information Architect — Enterprise IA for Copilot', slug: 'sharepoint-information-architect' }
        ],
        bundle: { code: 'SHARECOP-BUNDLE-001', name: '3-Day SharePoint Copilot Series Bundle', aud: 1995, savings: 'Save AUD 390 (~16%) vs individual' },
        contact: 'info@educ4te.com'
    },
    stripeConfig: {
    priceId: 'price_1U3WMnKu1nyc322U9ynUvILN',
    productId: 'prod_V3deexjlC4KpIS'
  },

    snapshot: {
        whoItsFor: [
            'SharePoint Site Owners new to Copilot',
            'Microsoft 365 administrators preparing a tenant for Copilot',
            'IT generalists about to enable Copilot licences',
            'Governance and compliance staff scoping the rollout'
        ],
        accreditation: 'Aligned to Microsoft 365 Copilot deployment guidance, Australian Privacy Act 1988, OAIC Notifiable Data Breaches scheme, and ACSC Essential Eight'
    },

    logistics: {
        classSize: 'Maximum 20 participants for hands-on lab work',
        requirements: [
            'SharePoint Site Owner familiarity (no admin certification required)',
            'Access to a Microsoft 365 tenant (trial, developer, or production sandbox)',
            'SharePoint Site Owner permissions on at least one site for lab exercises',
            'Computer with internet access and a modern browser'
        ]
    },

    overview: 'Microsoft 365 Copilot grounds every response in your SharePoint content using retrieval-augmented generation (RAG) — which means oversharing, broken permission inheritance, and missing sensitivity labels translate directly into Copilot leaks and inaccurate answers. This one-day Foundation course is the entry point of the EDUC4TE SharePoint Copilot Series. It is built for SharePoint Site Owners, Microsoft 365 administrators, and IT generalists who need a fast, practical grounding before Copilot reaches their tenant — without the depth of the Governance (SHARECOP-GOV-001) or Enterprise Architecture (SHARECOP-ARCH-001) courses. Across five short modules you will learn how Copilot retrieves from SharePoint, identify the five most common oversharing risk patterns, walk through a Day-0 readiness checklist, and understand at conceptual level how Microsoft Purview sensitivity labels, Restricted Content Discovery (RCD) and SharePoint Advanced Management (SAM) fit into the bigger Copilot picture. Australian organisations also learn how Copilot intersects with the Privacy Act 1988, OAIC Notifiable Data Breaches scheme, and ACSC Essential Eight. The course closes with a hands-on Day-0 readiness checklist lab so every participant leaves with a completed readiness assessment for at least one SharePoint site. Hands-on labs for Purview SAM, RCD, RSS, and SharePoint Agents live in the Governance and Architecture courses — this Foundation course tells you when to escalate to them.',

    outcomes: [
        'Explain in plain language how Microsoft 365 Copilot grounds responses in SharePoint content using retrieval-augmented generation (RAG)',
        'Identify the five most common SharePoint oversharing risk patterns that cause Copilot data leaks',
        'Complete a Copilot Day-0 readiness checklist for a SharePoint site or site collection',
        'Describe how SharePoint permission inheritance, unique permissions, and broken inheritance impact Copilot results',
        'Map Microsoft Purview sensitivity labels at conceptual level and explain how they gate Copilot access',
        'Recognise when a Copilot readiness issue should be escalated to the SHARECOP-GOV-001 Governance course or SHARECOP-ARCH-001 Architecture course',
        'Apply Privacy Act 1988, OAIC Notifiable Data Breaches scheme, and ACSC Essential Eight guidance to early-stage Copilot planning',
        'Brief your wider IT and governance team using a shared Copilot readiness vocabulary and checklist'
    ],

    audience: [
        'SharePoint Site Owners about to be exposed to Microsoft 365 Copilot',
        'Microsoft 365 administrators preparing a tenant for Copilot rollout',
        'IT generalists supporting SharePoint without a deep governance background',
        'Compliance and risk staff scoping Copilot implications for Australian privacy obligations',
        'Project managers and change leads preparing internal communications for Copilot launch',
        'Anyone planning to attend SHARECOP-GOV-001 or SHARECOP-ARCH-001 who wants the foundations first'
    ],

    prerequisites: [
        'SharePoint Site Owner familiarity (creating libraries, managing simple permissions)',
        'Basic awareness of Microsoft 365 services (SharePoint, Teams, OneDrive)',
        'No prior Microsoft 365 Copilot, Purview, or AI experience required',
        'No SharePoint Online admin certification required'
    ],

    modules: [
        {
            title: 'Copilot Grounding Fundamentals and RAG Primer',
            duration: '1.5 hours',
            topics: [
                'What Microsoft 365 Copilot actually is (and what it is not)',
                'Retrieval-augmented generation (RAG) explained for SharePoint practitioners',
                'How Copilot retrieves from SharePoint, OneDrive, Teams, and the Microsoft Graph',
                'Why poor SharePoint hygiene shows up immediately in Copilot answers',
                'Walkthrough: a single Copilot prompt traced back to its SharePoint sources'
            ]
        },
        {
            title: 'Permission Model and Oversharing 101',
            duration: '1.5 hours',
            topics: [
                'SharePoint permission inheritance, unique permissions, and broken inheritance',
                'The five most common oversharing patterns that leak through Copilot',
                'Everyone Except External Users, Company-Wide links, and legacy "Everyone" groups',
                'Site-level vs item-level permissions in a Copilot world',
                'Lab 1: Spot the oversharing risks in a sample SharePoint site (20 min)'
            ]
        },
        {
            title: 'Hub Sites and Content Types — Why AI Cares',
            duration: '1.5 hours',
            topics: [
                'Hub site basics and why structure matters for Copilot discoverability',
                'Content types and metadata at a conceptual level (no taxonomy design)',
                'Why "one giant Documents library" is the worst Copilot pattern',
                'Naming conventions and folder vs metadata trade-offs for AI',
                'When to escalate to SHARECOP-ARCH-001 for enterprise IA design'
            ]
        },
        {
            title: 'Sensitivity Labels and the Purview Teaser',
            duration: '1.5 hours',
            topics: [
                'Microsoft Purview sensitivity labels at conceptual level',
                'How labels gate Copilot access to documents and emails',
                'Introduction to SharePoint Advanced Management (SAM) and Restricted Content Discovery (RCD), and why Restricted SharePoint Search is retiring in favour of RCD',
                'A first look at SharePoint Agents and where they sit in the Copilot stack',
                'Privacy Act 1988, OAIC NDB scheme, and ACSC Essential Eight overlay on Copilot rollout',
                'When to escalate to SHARECOP-GOV-001 for hands-on SAM, RCD, and permission remediation labs'
            ]
        },
        {
            title: 'Day-0 Readiness Checklist Lab',
            duration: '2 hours',
            topics: [
                'Walk-through of the EDUC4TE SharePoint Copilot Day-0 Readiness Checklist',
                'Scoring your site against the five oversharing patterns',
                'Quick-win remediations a Site Owner can apply without admin escalation',
                'Building an "issues to raise with M365 admins" register',
                'Lab 2: Complete the Day-0 Readiness Checklist for one of your own SharePoint sites (45 min)',
                'Decision tree: stay at Foundation, or progress to Governance (GOV-001) or Architecture (ARCH-001)'
            ]
        }
    ],

    format: {
        type: '1-day live instructor-led workshop, 2 x half-day online delivery, or lunch-and-learn (8 x 1-hour sessions over 8 days)',
        includes: [
            '8 hours of instructor-led training with live SharePoint walkthroughs',
            '2 hands-on labs with step-by-step exercise guides',
            'EDUC4TE SharePoint Copilot Day-0 Readiness Checklist (PDF + editable)',
            'Oversharing risk pattern cheat sheet',
            'Copilot vocabulary glossary for site owners and admins',
            'Escalation decision tree (Foundation → Governance → Architecture)',
            'Certificate of completion',
            'Access to course materials for 12 months'
        ]
    },

    nextIntake: {
        date: 'Register your interest — we will confirm the next cohort',
        details: 'Full-day instructor-led workshop (9:00 AM to 5:00 PM AEST) or 2 x half-day online delivery'
    },

    faqs: [
        {
            question: 'How is the Foundation course different from SHARECOP-GOV-001 and SHARECOP-ARCH-001?',
            answer: 'Foundation (SHARECOP-FND-001) is the 1-day entry point: it teaches the language of Copilot, the five oversharing risk patterns, and a Day-0 readiness checklist for a single site. Governance (SHARECOP-GOV-001) is hands-on SharePoint Advanced Management (SAM), Restricted Content Discovery (RCD), and permission remediation for a SharePoint environment. Architecture (SHARECOP-ARCH-001) is enterprise-scale information architecture for Copilot across many sites and hubs. Most people start with Foundation, then choose Governance, Architecture, or the 3-day bundle.'
        },
        {
            question: 'What are the prerequisites for this Foundation course?',
            answer: 'None beyond basic SharePoint Site Owner familiarity. You do not need to be a Microsoft 365 administrator, hold any Microsoft certifications, or have prior experience with Copilot, Purview, or AI. If you can create a SharePoint library and add a few users to it, you are ready for this course.'
        },
        {
            question: 'Can I skip straight to SHARECOP-GOV-001 if I am already a senior admin?',
            answer: 'Yes — but only if you already have hands-on experience with permissions audits, sensitivity labels, and SharePoint admin tooling. If you are a senior M365 admin who has previously run an oversharing remediation programme, you can usually start at Governance. If you have never run a permissions audit or touched Microsoft Purview, Foundation is the faster path overall.'
        },
        {
            question: 'Does this course cover the Australian Privacy Act 1988 and OAIC obligations?',
            answer: 'Yes, at conceptual level. We cover how Copilot intersects with the Privacy Act 1988, the OAIC Notifiable Data Breaches scheme, and the ACSC Essential Eight — and where Copilot oversharing crosses from "annoying" to "notifiable". Deep compliance configuration (sensitivity labels, DLP policies, audit logging) lives in SHARECOP-GOV-001.'
        },
        {
            question: 'Do I need my own Microsoft 365 tenant for the labs?',
            answer: 'Yes — either a production tenant where you have Site Owner rights on at least one site, a Microsoft 365 developer tenant, or a free Microsoft 365 trial. Lab 2 (the Day-0 Readiness Checklist) is far more valuable when run against a site you actually care about. We provide setup guidance with your pre-course briefing.'
        },
        {
            question: 'Is private-cohort pricing available for whole-team enrolment?',
            answer: 'Yes. Foundation is designed to give a SharePoint Site Owner, an M365 admin, and a compliance representative a shared vocabulary — so we strongly encourage groups. For 5 or more participants from the same organisation, contact info@educ4te.com for a private quote and in-house delivery options across Australia.'
        },
        {
            question: 'Is the 3-day SharePoint Copilot Series bundle better value?',
            answer: 'Yes if you know you need all three courses. The SHARECOP-BUNDLE-001 (Foundation + Governance + Architecture) is priced at AUD 1,995 — a saving of approximately AUD 390 (~16%) versus enrolling in each course separately. Many organisations send their Site Owner to Foundation only, their M365 admin to Foundation + Governance, and their Information Architect to all three.'
        },
        {
            question: 'Will I receive a credential after completing the course?',
            answer: 'Yes. Participants receive an EDUC4TE Certificate of Completion after finishing the course and submitting their Day-0 Readiness Checklist for at least one SharePoint site. The certificate is shareable on LinkedIn and recognised across the EDUC4TE SharePoint Copilot Series learning pathway.'
        },
      {
        question: 'Can this course be delivered as lunch-and-learn sessions?',
        answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
      }
    ]
};
